import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Chip,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import { Sync, CheckCircle, Error } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const AdminSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const [environment, setEnvironment] = React.useState('test');

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('navigation.settings')}
      </Typography>

      <Grid container spacing={3}>
        {/* Epicor Configuration */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('admin.epicorSettings')}
            </Typography>
            
            <FormControl fullWidth margin="normal">
              <InputLabel>{t('admin.environment')}</InputLabel>
              <Select
                value={environment}
                label={t('admin.environment')}
                onChange={(e) => setEnvironment(e.target.value)}
              >
                <MenuItem value="test">{t('admin.testEnvironment')}</MenuItem>
                <MenuItem value="production">{t('admin.productionEnvironment')}</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              margin="normal"
              label={t('admin.apiUrl')}
              value="https://mowilex-pilot.epicorsaas.com/server/api/v2"
              disabled={environment === 'test'}
            />

            <TextField
              fullWidth
              margin="normal"
              label="API Username"
              value="admin_user"
            />

            <TextField
              fullWidth
              margin="normal"
              label="API Password"
              type="password"
              value="••••••••••"
            />

            <Box sx={{ mt: 2 }}>
              <Button variant="contained" sx={{ mr: 1 }}>
                {t('common.save')}
              </Button>
              <Button variant="outlined">
                Test Connection
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Sync Management */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Sync Management
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CheckCircle color="success" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">Customer Sync</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Last sync: 5 minutes ago
                  </Typography>
                  <Chip label="Active" color="success" size="small" sx={{ mt: 1 }} />
                </CardContent>
                <CardActions>
                  <Button size="small" startIcon={<Sync />}>
                    {t('admin.syncNow')}
                  </Button>
                </CardActions>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CheckCircle color="success" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">Product Sync</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Last sync: 15 minutes ago
                  </Typography>
                  <Chip label="Active" color="success" size="small" sx={{ mt: 1 }} />
                </CardContent>
                <CardActions>
                  <Button size="small" startIcon={<Sync />}>
                    {t('admin.syncNow')}
                  </Button>
                </CardActions>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Error color="error" sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">Order Sync</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Last sync failed: 2 hours ago
                  </Typography>
                  <Chip label="Error" color="error" size="small" sx={{ mt: 1 }} />
                </CardContent>
                <CardActions>
                  <Button size="small" startIcon={<Sync />}>
                    Retry Sync
                  </Button>
                </CardActions>
              </Card>
            </Box>
          </Paper>
        </Grid>

        {/* System Configuration */}
        <Grid size={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('admin.systemSettings')}
            </Typography>
            
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Default Language"
                  value="Bahasa Indonesia"
                  margin="normal"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Session Timeout (minutes)"
                  value="60"
                  type="number"
                  margin="normal"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Max File Upload Size (MB)"
                  value="10"
                  type="number"
                  margin="normal"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Support Email"
                  value="support@mowilex.com"
                  margin="normal"
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Button variant="contained">
                {t('common.save')} System Settings
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminSettingsPage;